/**
 * The five sections of "Add to downline", top to bottom on one page.
 *
 * These were four wizard steps. Every field, cap, hint and explanatory sentence
 * is carried over verbatim; what is gone is the gating — you can see and answer
 * all of it at once, in any order. The one thing role still controls is
 * VISIBILITY: bet limits and game switches are member-level settings, so on an
 * agent the whole section is absent rather than shown greyed out. That was the
 * wizard's best idea and it survives the collapse intact.
 */

import type { ReactNode } from 'react'
import {
  Card,
  Chip,
  Field,
  Icon,
  Input,
  Legend,
  MoneyInput,
  RoleChip,
  Textarea,
  Toggle,
} from '@w7/ui'
import { Amount } from '@/app/bits'
import { EMPTY, money, moneyCompact, pct } from '@/lib/format'
import { GAME_LABEL, ROLE_LABEL, roleDepth } from '@/data/types'
import type { Account, GameSwitches, Role } from '@/data/types'
import { parseNumber, parentMaxBet, type Availability, type CreateSummary, type CreateValues } from './summary'

export const GAME_KEYS: (keyof GameSwitches)[] = [
  'casino',
  'cardGame',
  'liveGame',
  'sportsbook',
  'premiumSportsbook',
]

/* ---------------------------------------------------------------- the map -- */

export interface SectionMeta {
  id: string
  label: string
  /** The validated fields that belong to this section — drives the checklist state. */
  fields: string[]
  /** What the checklist and the anchor nav focus when they navigate here. */
  firstControl: string | null
  memberOnly?: boolean
}

/** One list, read by the page, the rail checklist and the anchor nav. */
export const SECTIONS: SectionMeta[] = [
  { id: 'role', label: 'Role & identity', fields: ['username'], firstControl: 'role' },
  { id: 'credit', label: 'Credit', fields: ['creditLimit'], firstControl: 'creditLimit' },
  {
    id: 'rates',
    label: 'Rates & commission',
    fields: ['share', 'fancy', 'bookmaker', 'matchOdds'],
    firstControl: 'share',
  },
  {
    id: 'games',
    label: 'Games & bet limits',
    fields: ['minBet', 'maxBet'],
    firstControl: 'minBet',
    memberOnly: true,
  },
  { id: 'review', label: 'Review', fields: [], firstControl: null },
]

/* ------------------------------------------------------------- split bars -- */

/** A proportion bar — "of this whole, this much is that". */
export function SplitBar({
  label,
  segments,
}: {
  label: string
  segments: { id: string; value: number; color: string }[]
}) {
  const total = segments.reduce((sum, seg) => sum + Math.max(0, seg.value), 0)
  return (
    <div className="w7-splitbar" role="img" aria-label={label}>
      {segments.map((seg) => {
        const width = total > 0 ? (Math.max(0, seg.value) / total) * 100 : 0
        return width > 0 ? (
          <span key={seg.id} style={{ width: `${width}%`, background: seg.color }} />
        ) : null
      })}
    </div>
  )
}

export const SEG_ALLOC = 'var(--w7-orange)'
export const SEG_REST = 'var(--w7-border-strong)'
/* Over the ceiling, matching how the kit's Meter flips above 100% — an
   over-allocation must look over, not merely full. */
export const SEG_OVER = 'var(--w7-loss)'
export const SEG_YOU = 'var(--w7-c1)'
export const SEG_THEM = 'var(--w7-c3)'

/* ------------------------------------------------------------------- ctx -- */

export interface SectionCtx {
  parent: Account
  role: Role
  roles: Role[]
  values: CreateValues
  set: <K extends keyof CreateValues>(key: K, value: CreateValues[K]) => void
  /** Blur handler. Marks a field answered ONLY when it carries a value. */
  answer: (field: string, value: string) => void
  /** The error to SHOW — null until the field has been answered or Create pressed. */
  errorFor: (field: string) => string | null
  register: (field: string) => (el: HTMLElement | null) => void
  availability: Availability
  summary: CreateSummary
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="w7-create-section" aria-labelledby={`${id}-h`} tabIndex={-1}>
      <h2 id={`${id}-h`} className="w7-create-h2">
        {title}
      </h2>
      {children}
    </section>
  )
}

/* ------------------------------------------------- 1 · role and identity -- */

export function RoleIdentitySection({ ctx }: { ctx: SectionCtx }) {
  const { parent, role, roles, values, set, answer, errorFor, register, availability } = ctx
  const trimmed = values.username.trim()
  return (
    <Section id="role" title="Role & identity">
      <Card
        title="Level"
        subtitle={`You are ${ROLE_LABEL[parent.role]}. Ladder: only the next rung down, or a Member.`}
      >
        <div className="w7-optiongrid">
          {roles.map((option, index) => (
            <button
              key={option}
              type="button"
              className="w7-option"
              // Switching level hides the member-only section but keeps every
              // value typed into it — explore a different level and nothing you
              // wrote is destroyed. The form being replaced wiped it.
              ref={index === 0 ? register('role') : undefined}
              aria-pressed={option === role}
              onClick={() => set('role', option)}
            >
              <span className="w7-option-head">
                <RoleChip role={option} label={ROLE_LABEL[option]} />
                {option === role ? <Icon name="check" size={16} /> : null}
              </span>
              <span className="w7-option-note">
                {option === 'player'
                  ? 'Member (bottom of the ladder). Places bets; no downline of their own.'
                  : `Next rung under you. Stored as level ${roleDepth(option) + 1} — not a skip-level.`}
              </span>
            </button>
          ))}
        </div>
        <p className="w7-card-sub" style={{ marginTop: 'var(--w7-s3)' }}>
          Company Master → Triple Master → Double Master → Super Master → Master → Member. As{' '}
          {ROLE_LABEL[parent.role]} you may create {roles.map((r) => ROLE_LABEL[r]).join(' or ')}.{' '}
          {ROLE_LABEL[role]} will sit directly under {parent.username}.
        </p>
      </Card>

      <Card title="Identity" subtitle="How this account is named and found.">
        <div className="w7-grid w7-grid-2">
          <Field
            label="Username"
            required
            hint="Three to twenty characters: lowercase letters, digits, dot or underscore, starting with a letter."
            error={errorFor('username')}
            aside={
              trimmed && availability !== 'idle' ? (
                <span className="w7-avail" data-state={availability}>
                  {availability === 'checking' ? (
                    'Checking'
                  ) : availability === 'free' ? (
                    <>
                      <Icon name="check" size={13} /> Available
                    </>
                  ) : (
                    <>
                      <Icon name="close" size={13} /> Taken
                    </>
                  )}
                </span>
              ) : null
            }
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                ref={register('username')}
                aria-describedby={describedBy}
                invalid={invalid}
                autoComplete="off"
                spellCheck={false}
                placeholder="e.g. rk.master01"
                value={values.username}
                onChange={(event) => set('username', event.target.value.replace(/\s/g, '').toLowerCase())}
                onBlur={(event) => answer('username', event.target.value)}
              />
            )}
          </Field>

          <Field label="Display name" hint="Shown in tables and statements. Defaults to the username.">
            {({ id, describedBy }) => (
              <Input
                id={id}
                ref={register('displayName')}
                aria-describedby={describedBy}
                autoComplete="off"
                placeholder={trimmed || 'Optional'}
                value={values.displayName}
                onChange={(event) => set('displayName', event.target.value)}
              />
            )}
          </Field>
        </div>

        <div style={{ marginTop: 'var(--w7-s4)' }}>
          <Field
            label="Comments"
            hint="Free text kept on the account — who introduced them, what was agreed."
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                placeholder="Optional"
                value={values.comments}
                onChange={(event) => set('comments', event.target.value)}
              />
            )}
          </Field>
        </div>
      </Card>
    </Section>
  )
}

/* ------------------------------------------------------------ 2 · credit -- */

export function CreditSection({ ctx }: { ctx: SectionCtx }) {
  const { parent, role, values, set, answer, errorFor, register, summary } = ctx
  const isMember = role === 'player'

  return (
    <Section id="credit" title="Credit">
      <Card
        title="Credit limit"
        subtitle={
          isMember
            ? 'The most this member may ever hold. Bets are rejected once their exposure passes it.'
            : 'The ceiling for this agent and everything beneath it. They allocate their own downline out of this.'
        }
      >
        {/* The old form asked for five credit limits, one per level, and showed
            all five whatever role you had picked. Only the one that applies to
            the chosen level is here. */}
        <Field
          label={`Credit limit for this ${ROLE_LABEL[role]}`}
          required
          hint={`Up to ${money(parent.balance)} — you cannot allocate a ceiling above what you hold. Setting it moves no money; credit is given separately.`}
          error={errorFor('creditLimit')}
          aside={<Chip size="sm">Max {moneyCompact(parent.balance)}</Chip>}
        >
          {({ id, describedBy, invalid }) => (
            <MoneyInput
              id={id}
              ref={register('creditLimit')}
              aria-describedby={describedBy}
              invalid={invalid}
              value={values.creditLimit}
              onChange={(event) => set('creditLimit', event.target.value)}
              onBlur={(event) => answer('creditLimit', event.target.value)}
            />
          )}
        </Field>

        <div style={{ marginTop: 'var(--w7-s5)' }}>
          <div className="w7-row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <span className="w7-micro">Your balance</span>
            <Amount value={parent.balance} />
          </div>
          <SplitBar
            label={`Of your ${money(parent.balance)}, ${money(summary.allocating)} is being allocated`}
            segments={[
              {
                id: 'alloc',
                value: summary.allocating,
                color: summary.overAllocated ? SEG_OVER : SEG_ALLOC,
              },
              { id: 'rest', value: summary.remaining, color: SEG_REST },
            ]}
          />
          <div style={{ marginTop: 'var(--w7-s3)' }}>
            <Legend
              items={[
                {
                  label: 'Allocating',
                  color: summary.overAllocated ? SEG_OVER : SEG_ALLOC,
                  // Nothing typed yet is not the same as a limit of zero.
                  value: money(summary.creditLimit === null ? null : summary.allocating),
                },
                { label: 'Remaining', color: SEG_REST, value: money(summary.remaining) },
              ]}
            />
          </div>
        </div>
      </Card>
    </Section>
  )
}

/* ------------------------------------------------------------- 3 · rates -- */

export function RatesSection({ ctx }: { ctx: SectionCtx }) {
  const { parent, values, set, answer, errorFor, register, summary } = ctx
  const { yourShare, theirShare, restOfChain, maxShare } = summary
  const maxCommission = parent.commission

  const commissionField = (
    field: 'fancy' | 'bookmaker' | 'matchOdds',
    label: string,
    max: number,
  ) => (
    <Field label={label} hint={`Up to ${pct(max, 2)}.`} error={errorFor(field)}>
      {({ id, describedBy, invalid }) => (
        <Input
          id={id}
          ref={register(field)}
          aria-describedby={describedBy}
          invalid={invalid}
          numeric
          suffix="%"
          value={values[field]}
          onChange={(event) => set(field, event.target.value)}
          onBlur={(event) => answer(field, event.target.value)}
        />
      )}
    </Field>
  )

  return (
    <Section id="rates" title="Rates & commission">
      <Card
        title="Share"
        subtitle={`A member's loss is split into a hundred points down the chain. You keep ${pct(yourShare, 2)} of it whatever you set here; this decides what the new account keeps.`}
      >
        <div className="w7-grid w7-grid-2">
          <Field
            label="Their share"
            required
            hint={`Between 0% and ${pct(maxShare, 2)} — everything except the slice you keep.`}
            error={errorFor('share')}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                ref={register('share')}
                aria-describedby={describedBy}
                invalid={invalid}
                numeric
                suffix="%"
                value={values.share}
                onChange={(event) => set('share', event.target.value)}
                onBlur={(event) => answer('share', event.target.value)}
              />
            )}
          </Field>
          {/* Mirrors the Field skeleton — label row, then a 38px band — so the
              slider's centreline lands on the input's. */}
          <div className="w7-field">
            <span className="w7-field-label">Drag to set</span>
            <span className="w7-row" style={{ height: 38 }}>
              <input
                className="w7-range"
                type="range"
                min={0}
                max={Math.max(1, maxShare)}
                step={0.5}
                value={Math.min(theirShare, Math.max(1, maxShare))}
                aria-label="Their share, percentage points"
                onChange={(event) => {
                  set('share', event.target.value)
                  answer('share', event.target.value)
                }}
              />
            </span>
          </div>
        </div>

        <div style={{ marginTop: 'var(--w7-s5)' }}>
          <SplitBar
            label={`You keep ${pct(yourShare, 2)}, they keep ${pct(theirShare, 2)}`}
            segments={[
              { id: 'you', value: yourShare, color: SEG_YOU },
              { id: 'them', value: theirShare, color: SEG_THEM },
              { id: 'rest', value: restOfChain, color: SEG_REST },
            ]}
          />
          <div style={{ marginTop: 'var(--w7-s3)' }}>
            <Legend
              items={[
                { label: 'You keep', color: SEG_YOU, value: pct(yourShare, 2) },
                { label: 'They keep', color: SEG_THEM, value: pct(theirShare, 2) },
                { label: 'Rest of the chain', color: SEG_REST, value: pct(restOfChain, 2) },
              ]}
            />
          </div>
        </div>
      </Card>

      <Card
        title="Commission"
        subtitle="Paid down to this account on settled turnover. Each rate is capped at your own."
      >
        <div className="w7-grid w7-grid-3">
          {commissionField('fancy', 'Fancy', maxCommission.fancy)}
          {commissionField('bookmaker', 'Bookmaker', maxCommission.bookmaker)}
          {commissionField('matchOdds', 'Match odds', maxCommission.matchOdds)}
        </div>
      </Card>
    </Section>
  )
}

/* ------------------------------------------ 4 · games and bet limits (member) */

export function GamesSection({ ctx }: { ctx: SectionCtx }) {
  const { parent, values, set, answer, errorFor, register } = ctx
  const ceiling = parentMaxBet(parent)

  return (
    <Section id="games" title="Games & bet limits">
      <Card title="Bet limits" subtitle="The stake range allowed on a single bet.">
        <div className="w7-grid w7-grid-2">
          <Field
            label="Minimum bet"
            required
            hint="Stakes below this are rejected."
            error={errorFor('minBet')}
          >
            {({ id, describedBy, invalid }) => (
              <MoneyInput
                id={id}
                ref={register('minBet')}
                aria-describedby={describedBy}
                invalid={invalid}
                value={values.minBet}
                onChange={(event) => set('minBet', event.target.value)}
                onBlur={(event) => answer('minBet', event.target.value)}
              />
            )}
          </Field>
          <Field
            label="Maximum bet"
            required
            hint={
              ceiling === null
                ? 'Stakes above this are rejected.'
                : `Cannot exceed your own maximum of ${money(ceiling, { round: true })}.`
            }
            error={errorFor('maxBet')}
          >
            {({ id, describedBy, invalid }) => (
              <MoneyInput
                id={id}
                ref={register('maxBet')}
                aria-describedby={describedBy}
                invalid={invalid}
                value={values.maxBet}
                onChange={(event) => set('maxBet', event.target.value)}
                onBlur={(event) => answer('maxBet', event.target.value)}
              />
            )}
          </Field>
        </div>
      </Card>

      <Card
        title="Games"
        subtitle="What this member may play. Everything off means they can hold credit but place nothing."
        flush
      >
        <ul className="w7-list">
          {GAME_KEYS.map((key) => (
            <li key={key}>
              <span className="w7-list-row">
                <span>{GAME_LABEL[key]}</span>
                <Toggle
                  checked={values.games[key]}
                  label={GAME_LABEL[key]}
                  onChange={(next) => set('games', { ...values.games, [key]: next })}
                />
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </Section>
  )
}

/* ------------------------------------------------------------ 5 · review -- */

export function ReviewSection({ ctx, banner }: { ctx: SectionCtx; banner?: ReactNode }) {
  const { parent, role, values, summary } = ctx
  const isMember = role === 'player'
  const trimmed = values.username.trim()
  const minValue = parseNumber(values.minBet)
  const maxValue = parseNumber(values.maxBet)

  return (
    <Section id="review" title="Review">
      {/* The read-back is never collapsed. The rail carries the live
          consequence, so nothing here depends on being scrolled to. */}
      {banner}
      <Card title="Review" subtitle="Everything that will be recorded.">
        <dl className="w7-dl">
          <dt>Level</dt>
          <dd>
            <RoleChip role={role} label={ROLE_LABEL[role]} />
          </dd>
          <dt>Username</dt>
          <dd>{trimmed || EMPTY}</dd>
          <dt>Display name</dt>
          <dd>{values.displayName.trim() || trimmed || EMPTY}</dd>
          <dt>Sits under</dt>
          <dd>
            {parent.username} · {ROLE_LABEL[parent.role]}
          </dd>
          <dt>Credit limit</dt>
          <dd>
            <Amount value={summary.creditLimit} />
          </dd>
          <dt>Left with you</dt>
          <dd>
            <Amount value={summary.remaining} />
          </dd>
          <dt>Share</dt>
          <dd>
            they keep {pct(summary.theirShare, 2)} · you keep {pct(summary.yourShare, 2)}
          </dd>
          <dt>Commission</dt>
          <dd>
            fancy {pct(parseNumber(values.fancy), 2)} · bookmaker{' '}
            {pct(parseNumber(values.bookmaker), 2)} · match odds{' '}
            {pct(parseNumber(values.matchOdds), 2)}
          </dd>
          <dt>Bet limits</dt>
          <dd>
            {isMember ? (
              <>
                <Amount value={minValue} /> – <Amount value={maxValue} />
              </>
            ) : (
              'Set per member, not on an agent'
            )}
          </dd>
          <dt>Games</dt>
          <dd>
            {isMember
              ? GAME_KEYS.filter((key) => values.games[key])
                  .map((key) => GAME_LABEL[key])
                  .join(', ') || 'None enabled'
              : 'Set per member, not on an agent'}
          </dd>
          <dt>Comments</dt>
          <dd>{values.comments.trim() || EMPTY}</dd>
          <dt>Sign-in</dt>
          <dd>Not provisioned — issue #149</dd>
        </dl>
      </Card>
    </Section>
  )
}
